import React, { useEffect, useState, useCallback } from 'react';
import { Loader2, Search, Download, Trash2, Edit, X, Check, IdCard, Users, TrendingUp, Clock, Coins } from 'lucide-react';
import axios from 'axios';
import { getToken } from '../lib/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TIER_COLORS = {
  decouverte: 'bg-slate-100 text-slate-700 border-slate-200',
  essentielle: 'bg-orange-100 text-orange-700 border-orange-200',
  prestige: 'bg-amber-100 text-amber-800 border-amber-300',
};

const STATUS_COLORS = {
  active: 'bg-emerald-100 text-emerald-700',
  pending: 'bg-yellow-100 text-yellow-700',
  expired: 'bg-slate-100 text-slate-500',
  cancelled: 'bg-red-100 text-red-700',
};

const TIER_LABELS = { decouverte: 'Découverte', essentielle: 'Essentielle', prestige: 'Prestige' };
const STATUS_LABELS = { active: 'Actif', pending: 'En attente', expired: 'Expiré', cancelled: 'Annulé' };

const MembersManagement = () => {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ total: 0, active: 0, pending: 0, expired: 0, revenue_xof: 0 });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [tierFilter, setTierFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [editing, setEditing] = useState(null); // membre en cours d'édition

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set('q', q);
      if (tierFilter) params.set('tier', tierFilter);
      if (statusFilter) params.set('status', statusFilter);
      const { data } = await axios.get(`${API_URL}/api/admin/members?${params}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      setItems(data.items || []);
      setStats(data.stats || {});
    } catch (err) {
      console.error('load members', err);
    } finally {
      setLoading(false);
    }
  }, [q, tierFilter, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const handleExportCsv = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/members/export.csv`, {
        headers: { Authorization: `Bearer ${getToken()}` },
        responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `members_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      alert('Erreur export');
    }
  };

  const handleUpdate = async (memberId, updates) => {
    try {
      await axios.patch(`${API_URL}/api/admin/members/${memberId}`, updates, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      setEditing(null);
      load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Erreur mise à jour');
    }
  };

  const handleDelete = async (memberId, memberEmail) => {
    if (!window.confirm(`Supprimer ${memberEmail} ? Action irréversible.`)) return;
    try {
      await axios.delete(`${API_URL}/api/admin/members/${memberId}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Erreur suppression');
    }
  };

  return (
    <div className="space-y-6" data-testid="members-management">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <IdCard className="w-6 h-6 text-orange-500" />
            Cartes de membre
          </h2>
          <p className="text-sm text-slate-500 mt-1">Gestion des adhésions et exports</p>
        </div>
        <button
          onClick={handleExportCsv}
          className="flex items-center gap-2 px-4 py-2 rounded-xl bg-slate-900 text-white hover:bg-slate-800 transition-colors text-sm font-semibold"
          data-testid="members-export-csv"
        >
          <Download className="w-4 h-4" />
          Exporter CSV
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <StatCard icon={Users} label="Total" value={stats.total} color="text-slate-700" />
        <StatCard icon={TrendingUp} label="Actifs" value={stats.active || 0} color="text-emerald-600" />
        <StatCard icon={Clock} label="En attente" value={stats.pending || 0} color="text-yellow-600" />
        <StatCard icon={X} label="Expirés" value={stats.expired || 0} color="text-slate-500" />
        <StatCard
          icon={Coins}
          label="Revenus"
          value={`${(stats.revenue_xof || 0).toLocaleString('fr-FR')} F`}
          color="text-orange-600"
          fullWidth
        />
      </div>

      {/* Filtres */}
      <div className="bg-white rounded-2xl p-4 border border-slate-100 flex flex-wrap gap-3 items-center">
        <div className="flex-1 min-w-[200px] relative">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Rechercher nom, email, numéro…"
            className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 focus:border-orange-400 focus:ring-2 focus:ring-orange-100 outline-none text-sm"
            data-testid="members-search"
          />
        </div>
        <select
          value={tierFilter}
          onChange={(e) => setTierFilter(e.target.value)}
          className="px-4 py-2.5 rounded-xl border border-slate-200 focus:border-orange-400 outline-none text-sm bg-white"
          data-testid="members-filter-tier"
        >
          <option value="">Toutes formules</option>
          <option value="decouverte">Découverte</option>
          <option value="essentielle">Essentielle</option>
          <option value="prestige">Prestige</option>
        </select>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-2.5 rounded-xl border border-slate-200 focus:border-orange-400 outline-none text-sm bg-white"
          data-testid="members-filter-status"
        >
          <option value="">Tous statuts</option>
          <option value="active">Actif</option>
          <option value="pending">En attente</option>
          <option value="expired">Expiré</option>
          <option value="cancelled">Annulé</option>
        </select>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
        {loading ? (
          <div className="p-12 flex items-center justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-orange-500" />
          </div>
        ) : items.length === 0 ? (
          <div className="p-12 text-center text-slate-500">
            <IdCard className="w-10 h-10 text-slate-300 mx-auto mb-2" />
            Aucun membre trouvé.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600 uppercase text-xs tracking-wider">
                <tr>
                  <th className="text-left px-4 py-3">N° Membre</th>
                  <th className="text-left px-4 py-3">Nom</th>
                  <th className="text-left px-4 py-3">Contact</th>
                  <th className="text-left px-4 py-3">Formule</th>
                  <th className="text-left px-4 py-3">Statut</th>
                  <th className="text-left px-4 py-3">Créé le</th>
                  <th className="text-right px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {items.map((m) => (
                  <tr key={m.id} className="hover:bg-slate-50 transition-colors" data-testid={`member-row-${m.id}`}>
                    <td className="px-4 py-3 font-mono text-xs text-slate-700">{m.member_number}</td>
                    <td className="px-4 py-3">
                      <div className="font-semibold text-slate-900">
                        {m.first_name} {m.last_name}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-slate-700">{m.email}</div>
                      <div className="text-xs text-slate-500">{m.phone}</div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold border ${TIER_COLORS[m.tier] || TIER_COLORS.decouverte}`}>
                        {TIER_LABELS[m.tier] || m.tier}
                      </span>
                      {m.amount > 0 && (
                        <div className="text-xs text-slate-500 mt-1">
                          {m.amount.toLocaleString('fr-FR')} FCFA
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold ${STATUS_COLORS[m.status] || STATUS_COLORS.pending}`}>
                        {STATUS_LABELS[m.status] || m.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-500 text-xs">
                      {m.created_at ? new Date(m.created_at).toLocaleDateString('fr-FR') : '—'}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => setEditing(m)}
                          className="p-1.5 rounded-lg text-slate-500 hover:bg-orange-50 hover:text-orange-600 transition-colors"
                          data-testid={`member-edit-${m.id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(m.id, m.email)}
                          className="p-1.5 rounded-lg text-slate-500 hover:bg-red-50 hover:text-red-600 transition-colors"
                          data-testid={`member-delete-${m.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal édition */}
      {editing && (
        <EditModal
          member={editing}
          onClose={() => setEditing(null)}
          onSave={(updates) => handleUpdate(editing.id, updates)}
        />
      )}
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, color, fullWidth }) => (
  <div className={`bg-white rounded-2xl p-4 border border-slate-100 ${fullWidth ? 'col-span-2 lg:col-span-1' : ''}`}>
    <div className="flex items-center gap-2 text-xs text-slate-500 uppercase tracking-widest mb-2">
      <Icon className={`w-4 h-4 ${color}`} />
      {label}
    </div>
    <div className={`text-2xl font-bold ${color}`}>{value}</div>
  </div>
);

const EditModal = ({ member, onClose, onSave }) => {
  const [form, setForm] = useState({
    tier: member.tier,
    status: member.status,
    notes: member.notes || '',
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(form);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="member-edit-modal">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-slate-900">Modifier {member.first_name} {member.last_name}</h3>
          <button onClick={onClose} className="p-1 rounded-full hover:bg-slate-100">
            <X className="w-4 h-4" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1.5">Formule</label>
            <select
              value={form.tier}
              onChange={(e) => setForm({ ...form, tier: e.target.value })}
              className="w-full px-3 py-2.5 rounded-xl border border-slate-200 focus:border-orange-400 outline-none text-sm bg-white"
            >
              <option value="decouverte">Découverte (Gratuit)</option>
              <option value="essentielle">Essentielle (25 000 F)</option>
              <option value="prestige">Prestige (75 000 F)</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1.5">Statut</label>
            <select
              value={form.status}
              onChange={(e) => setForm({ ...form, status: e.target.value })}
              className="w-full px-3 py-2.5 rounded-xl border border-slate-200 focus:border-orange-400 outline-none text-sm bg-white"
            >
              <option value="active">Actif</option>
              <option value="pending">En attente</option>
              <option value="expired">Expiré</option>
              <option value="cancelled">Annulé</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1.5">Notes internes</label>
            <textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 rounded-xl border border-slate-200 focus:border-orange-400 outline-none text-sm resize-none"
              placeholder="Commentaire admin uniquement…"
            />
          </div>
          <div className="flex gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2.5 rounded-xl border border-slate-200 hover:bg-slate-50 text-slate-700 font-semibold text-sm"
            >
              Annuler
            </button>
            <button
              type="submit"
              className="flex-1 py-2.5 rounded-xl bg-orange-500 hover:bg-orange-600 text-white font-semibold text-sm flex items-center justify-center gap-2"
              data-testid="member-edit-save"
            >
              <Check className="w-4 h-4" />
              Enregistrer
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MembersManagement;
